import { AppButton, FilterBar, PageHeader } from "../../components/ui";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField
} from "@mui/material";
import "react-tabulator/lib/styles.css";
import {
  ReactTabulator,
  ReactTabulatorOptions,
} from "react-tabulator";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ReportRequest,
  LeadsService,
  ReportTypes,
  LeadsUserReport,
  LeadsDateReport,
  ClioService,
  ApplicationRole,
  OpenAPI,
  ReportModel,
} from "../../api";
import { DatePicker } from "@mui/x-date-pickers";
import { TabsHelpers } from "../../components/basic/TabsHelpers";
import FilterableSelect from "../../components/basic/FilterableSelect";
import {
  ApSelect,
  ClioUserSelect,
  RaaSelect,
  SaSelect,
  SmaSelect,
  useClioUsers,
} from "../../components/shared/ClioSelects";
import {
  AllSelection,
  Presets,
  ReportDetails,
  ReportSource,
  ReportsData,
} from "./ReportsOptions";
import { HelpersService } from "../../services/Helpers";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { DateTime } from "luxon";
import "./Reports.scss";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useParams } from "react-router-dom";
import { MatterData } from "../../api/models/MatterData";
import { LitigationReportRequest } from "../../api/models/LitigationReportRequest";
import { LitigationReportTable, LitigationReportTableHandle } from "./LitigationFunnel/LitigationReportTable";

const MySwal = withReactContent(Swal);

interface ReportsProps {
  source: ReportSource;
}

export default function Reports(props: ReportsProps) {
  const [tab, setTab] = useState<number>(0);
  const [tableData, setTableData] = useState<
    LeadsUserReport[] | LeadsDateReport[] | ReportModel[]
  >([]);
  const [filteredTableData, setFilteredTableData] = useState<
    LeadsUserReport[] | LeadsDateReport[] | ReportModel[]
  >([]);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [selectedPreset, setSelectedPreset] = useState<any>("Month");
  const [selectedReportData, setSelectedReportData] = useState<ReportDetails>();
  const { users: usersList } = useClioUsers();
  const [selectedUser, setSelectedUser] = useState<string | null | undefined>(
    null
  );
  const [selectedAssignee, setSelectedAssignee] = useState<string | null | undefined>(
    null
  );
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<string>(AllSelection);
  const [selectedReport, setSelectedReport] = useState<ReportTypes>();

  // Litigation Funnel Report
  const [matterOptions, setMatterOptions] = useState<MatterData[]>([]);
  const [notesDirty, setNotesDirty] = useState(false);

  const [reportFilter, setReportFilter] = useState<LitigationReportRequest>({})
  const [search, setSearch] = useState<string>('')
  
  const dispatch = useDispatch();
  const { reportType } = useParams();

  const user = useSelector((state: any) => state.user?.user);
  const phoneId = user?.zoomExtPhoneNumber?.trim() || null;
  const userAdminRoles = [
    ApplicationRole.ADMIN,
    ApplicationRole.CLIO_ADMIN,
    ApplicationRole.CLIO_MANAGER,
  ];

  const litigationTableRef = useRef<LitigationReportTableHandle | null>(null);

  // @ts-ignore
  if (!window.DateTime) {
    // @ts-ignore
    window.DateTime = DateTime;
  }
  // @ts-ignore
  if (!window.luxon) {
    // @ts-ignore
    window.luxon = DateTime;
  }

  useEffect(() => {
    ClioService.getApiClioLitigationMatters(true).then((matters) => {
        setMatterOptions(matters.sort((a, b) => {
            const aName = `${a.display_number}`;
            const bName = `${b.display_number}`;
            return aName.localeCompare(bName)
        }));
    });
  }, [props.source]);

  useEffect(() => {
    if (!reportType) {
      return;
    }
    setSelectedReport(reportType as ReportTypes);
  }, [reportType]);

  useEffect(() => {
    setTableData([]);
    if (!selectedReport) {
      return;
    }
    setSelectedPreset("Month");
    if(selectedReport === ReportTypes.SOLES || selectedReport === ReportTypes.TASKS || selectedReport === ReportTypes.LITIGATION_FUNNEL){
      setStartDate(null);
      setEndDate(null);
    }
    else if (
      selectedReport === ReportTypes.HOURS ||
      selectedReport === ReportTypes.MATTER_STAGE_CHANGE
    ) {
      setStartDate(dayjs().startOf("month"));
      setEndDate(dayjs(new Date()));
    } else {
      clearData();
    }
    setSelectedReportData(
      ReportsData.find((data) => data.type === selectedReport)
    );
  }, [selectedReport]);

  const clearData = () => {
    setEndDate(
      reportType == ReportTypes.SETTLEMENT_LIT || reportType == ReportTypes.SETTLEMENT_PRELIT || reportType === ReportTypes.LITIGATION_FUNNEL
        ? null
        : dayjs(new Date())
    );
    setStartDate(null);
    setSelectedPreset(AllSelection);
    setSelectedUser(null);
    setSelectedAssignee(null);
    setSelectedPracticeArea(AllSelection);
  };

  useEffect(() => {
    if (selectedReport === ReportTypes.LITIGATION_FUNNEL) {
      const loweredSearch = search.toLocaleLowerCase();
      setFilteredTableData(tableData.map((tb) => ({
        ...tb,
        reportLevel2: (tb as ReportModel).reportLevel2?.filter(r2 => loweredSearch === '' || r2.level2Data5?.toLowerCase().includes(loweredSearch))
      })).filter(tb => ( (tb as ReportModel)?.reportLevel2?.length ?? 0) > 0));
    } else if (
      selectedReport === ReportTypes.LEADS_DATE &&
      selectedPracticeArea &&
      selectedPracticeArea !== AllSelection
    ) {
      setFilteredTableData(
        (tableData as LeadsDateReport[]).map((row) => {
          const leads = (row.leads ?? []).filter(
            (lead) => lead.practiceArea === selectedPracticeArea
          );
          return {
            ...row,
            leads,
            total: leads.length,
          };
        }).filter((row) => (row.leads?.length ?? 0) > 0)
      );
    } else {
      setFilteredTableData(tableData)
    }
  }, [tableData, search, selectedReport, selectedPracticeArea])

  useEffect(() => {
    if (selectedReport !== ReportTypes.LITIGATION_FUNNEL) return;

    const allFields =
      (selectedReportData?.columns ?? [])
        .slice(2)
        .map((c: any) => c?.field)
        .filter((f: any): f is string => typeof f === "string" && f.length > 0)
        .map((f) => f.toLowerCase());

    setReportFilter((prev) => ({
      ...prev,
      downloadColumns: allFields,
    }));
  }, [selectedReport, selectedReportData]);


  const updatePropertyNames = (data) => {
    return data.map((item) => {
      const updatedItem = { ...item };

      //user report
      if (updatedItem.leadsByStatus) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.leadsByStatus
        );
        delete updatedItem.leadsByStatus;
      }
      if (updatedItem.leadsByCategory) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.leadsByCategory
        );
        delete updatedItem.leadsByCategory;
      }

      if (updatedItem.leads) {
        updatedItem["_children"] = updatePropertyNames(updatedItem.leads);
        delete updatedItem.leads;
      }

      if (updatedItem.createdAt) {
        const parsed = dayjs(updatedItem.createdAt);
        updatedItem.createdAt = parsed.isValid()
          ? parsed.format("MM/DD/YYYY")
          : updatedItem.createdAt;
      }
      if (updatedItem.completedAt) {
        updatedItem.completedAt =
          updatedItem.completedAt == "N/A"
            ? "N/A"
            : new Date(updatedItem.completedAt).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
      }
      if (updatedItem.lastChangeAt) {
        updatedItem.lastChangeAt =
          updatedItem.lastChangeAt == "N/A"
            ? "N/A"
            : new Date(updatedItem.lastChangeAt).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              });
      }

      //Clio
      if (updatedItem.level4Currency) {
        updatedItem.level4Currency = `${
           isNaN(+updatedItem.level4Currency)
            ? "N/A"
            : new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(+updatedItem.level4Currency)
        }`;
      }
      if (
        (reportType == ReportTypes.SETTLEMENT_LIT ||
          reportType == ReportTypes.SETTLEMENT_PRELIT) &&
        updatedItem.totalClio
      ) {
        updatedItem.totalClio = `${
          updatedItem.totalClio == "N/A"
            ? "N/A"
            : new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(updatedItem.totalClio)
        }`;
      }

      if (!!updatedItem.total && !!updatedItem.totalClio) {
        updatedItem.totalHours = `${updatedItem.totalHours} || ${updatedItem.totalClioHours}`;
      }

      if (updatedItem.total === 0 && reportType === ReportTypes.LITIGATION_FUNNEL) {
        delete updatedItem.total
      }

      if (updatedItem.reportLevel2) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel2
        );
        delete updatedItem.reportLevel2;
      }
      if (updatedItem.reportLevel3) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel3
        );
        delete updatedItem.reportLevel3;
      }
      if (updatedItem.reportLevel4) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel4
        );
        delete updatedItem.reportLevel4;
      }
      if (updatedItem.reportLevel5) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel5
        );
        delete updatedItem.reportLevel5;
      }
      if (updatedItem.reportLevel6) {
        updatedItem["_children"] = updatePropertyNames(
          updatedItem.reportLevel6
        );
        delete updatedItem.reportLevel6;
      }

      return updatedItem;
    });
  };

  const loadData = () => {
    if (selectedReport === ReportTypes.EMPTY_MATTERS) {
      dispatch(startSpinner());
      ClioService.getApiClioEmptyMattersReport()
        .then((report) => {
          if (!!report) {
            setTableData(report);
            console.log(report);
          }
        })
        .catch(() =>
          MySwal.fire({
            icon: "error",
            title: (
              <p className="title">
                Error loading data. Please make sure you selected your user in
                Zoom Integration and try again in a couple minutes.
              </p>
            ),
          })
        )
        .finally(() => dispatch(stopSpinner()));
      return;
    }
    if (
      selectedReport === ReportTypes.OVERVIEW &&
      user?.roles?.filter((role) => userAdminRoles.includes(role)).length !==
        0 &&
      selectedUser === null
    ) {
      MySwal.fire({
        icon: "error",
        title: <p className="title">Please select staff</p>,
      });
      return;
    } else if (
      selectedReport === ReportTypes.OVERVIEW &&
      user?.roles?.filter((role) => userAdminRoles.includes(role)).length ===
        0 &&
      !phoneId
    ) {
      MySwal.fire({
        icon: "error",
        title: (
          <p className="title">
            Please ask an admin to assign your Zoom user before accessing
            reports
          </p>
        ),
      });
      return;
    }
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
      selectedStaff:
        selectedUser !== AllSelection
          ? usersList.find((user) => user.name === selectedUser)
          : null,
      endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      litigationFilter: reportFilter
    } as ReportRequest;
    if (props.source === ReportSource.Leads) {
      LeadsService.postApiLeadsLeadsReport(reportRequest)
        .then((report) => {
          if (!!report) {
            setTableData(report.leadsByUser ?? report.leadsByDate ?? []);
          }
        })
        .catch(() =>
          MySwal.fire({
            icon: "error",
            title: <p className="title">Error loading data</p>,
          })
        )
        .finally(() => dispatch(stopSpinner()));
    } else {
      reportRequest.zoomId = phoneId;
      ClioService.postApiClioUserReport(reportRequest)
        .then((report) => {
          if (!!report) {
            setTableData(report);
            console.log(report);
          }
        })
        .catch((error) => {
          console.log(error);
          MySwal.fire({
            icon: "error",
            title: <p className="title">Error loading data</p>,
          });
        })
        .finally(() => dispatch(stopSpinner()));
    }
  };

  const handlePresetChange = (event) => {
    setSelectedPreset(event.target.value);
    const currentDate = dayjs();
    let selectedDate;
    let endDate = dayjs(new Date());

    switch (event.target.value) {
      case "LastMonth":
        selectedDate = currentDate.startOf("month").add(-1, "month");
        endDate = currentDate.startOf("month").add(-1, "day");
        break;
      case "Month":
        selectedDate = currentDate.startOf("month");
        break;
      case "Week":
        selectedDate = currentDate.startOf("week");
        break;
      case "Day":
        selectedDate = currentDate.startOf("day");
        break;
      default:
        selectedDate = null;
        break;
    }
    setStartDate(selectedDate);
    setEndDate(endDate);
  };

  const tableOptions: ReactTabulatorOptions = {
    dataTree: true,
    dataTreeFilter: true,
    height: 1000,
    dataTreeStartExpanded: selectedReport === ReportTypes.LITIGATION_FUNNEL
  };

  const showLeadsTotalSummary =
    selectedReport === ReportTypes.LEADS_STAFF ||
    selectedReport === ReportTypes.LEADS_ATTORNEY ||
    selectedReport === ReportTypes.LEADS_DATE;

  const leadsTotalSum = useMemo(() => {
    if (!showLeadsTotalSummary) {
      return 0;
    }
    return (tableData as Array<{ total?: number }>).reduce(
      (sum, row) => sum + (Number(row?.total) || 0),
      0
    );
  }, [tableData, showLeadsTotalSummary]);

  const leadsCompletedSum = useMemo(() => {
    if (selectedReport !== ReportTypes.LEADS_DATE) {
      return 0;
    }
    return (tableData as LeadsDateReport[]).reduce(
      (sum, row) => sum + (Number(row?.completed) || 0),
      0
    );
  }, [tableData, selectedReport]);

  const DateRangeSelect = () => {
    return (
      <>
        <DatePicker
          disableFuture={reportType !== ReportTypes.SOLES}
          label="Start date"
          value={startDate}
          onChange={(date) => {
            setStartDate(date);
          }}
          slotProps={{
            textField: {
              size: "small",
              sx: { width: { xs: "100%", sm: 180 }, bgcolor: "background.paper" },
            },
          }}
        />
        <DatePicker
          disableFuture={reportType !== ReportTypes.SOLES && selectedReport !== ReportTypes.TASKS && selectedReport !== ReportTypes.LITIGATION_FUNNEL}
          label="End date"
          value={endDate}
          onChange={(date) => {
            setEndDate(date);
          }}
          slotProps={{
            textField: {
              size: "small",
              sx: { width: { xs: "100%", sm: 180 }, bgcolor: "background.paper" },
            },
          }}
        />
        <FormControl size="small" sx={{ width: { xs: "100%", sm: 180 }, bgcolor: "background.paper" }}>
          <InputLabel>Select preset</InputLabel>
          <Select
            value={selectedPreset ?? "All time"}
            key={selectedPreset}
            label="Select preset"
            onChange={handlePresetChange}
          >
            <MenuItem key={0} value={AllSelection}>
              Select All
            </MenuItem>
            {Object.keys(Presets)
              .filter((key) => isNaN(Number(key)))
              .map((key) => {
                return (
                  <MenuItem key={key} value={key}>
                    {HelpersService.getEnumKeyByEnumValue(
                      Presets,
                      Presets[key]
                    )}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </>
    );
  };

  const AddSelect = (hideAll: boolean = false) => {
    return (
      <ClioUserSelect
        label="Team Member"
        size="small"
        includeAll={!hideAll}
        allOptionLabel="Select None"
        allOptionValue={AllSelection}
        value={selectedUser ?? undefined}
        users={usersList}
        onChange={(userName) => setSelectedUser(userName as string)}
        minWidth={200}
        sx={{ bgcolor: "background.paper" }}
      />
    );
  };

  const AddLitigationFilters = () => {
    return (<>
      <DatePicker
        disableFuture={reportType !== ReportTypes.SOLES}
        label="Task Due Date From"
        value={startDate}
        onChange={(date) => {
          setStartDate(date);
        }}
        slotProps={{
          textField: {
            size: "small",
            sx: { width: { xs: "100%", sm: 180 }, bgcolor: "background.paper" },
          },
        }}
      />
      <DatePicker
        disableFuture={reportType !== ReportTypes.SOLES && selectedReport !== ReportTypes.TASKS && selectedReport !== ReportTypes.LITIGATION_FUNNEL}
        label="Task Due Date To"
        value={endDate}
        onChange={(date) => {
          setEndDate(date);
        }}
        slotProps={{
          textField: {
            size: "small",
            sx: { width: { xs: "100%", sm: 180 }, bgcolor: "background.paper" },
          },
        }}
      />
      <FilterableSelect
        label="Matter"
        size="small"
        value={reportFilter.matterId}
        options={matterOptions.map((matter) => ({
          value: matter.id,
          label: matter.display_number,
        }))}
        onChange={(matterId) =>
          setReportFilter((prev) => ({
            ...prev,
            matterId: matterId as number | undefined,
          }))
        }
        minWidth={200}
        sx={{ bgcolor: "background.paper" }}
      />
      <ClioUserSelect
        label="Task Assignee"
        size="small"
        allOptionLabel="Select None"
        allOptionValue={AllSelection}
        value={selectedAssignee ?? undefined}
        users={usersList}
        onChange={(assigneeName, user) => {
          const value = assigneeName as string;
          setSelectedAssignee(value);
          setReportFilter((prev) => ({
            ...prev,
            assignee: value !== AllSelection ? user ?? null : null,
          }));
        }}
        minWidth={180}
        sx={{ bgcolor: "background.paper" }}
      />
      <ApSelect
        size="small"
        includeNone
        value={reportFilter.ap}
        onChange={(ap) =>
          setReportFilter((prev) => ({
            ...prev,
            ap: ap as number | undefined,
          }))
        }
        minWidth={120}
        sx={{ bgcolor: "background.paper" }}
      />
      <RaaSelect
        size="small"
        includeNone
        value={reportFilter.raa}
        onChange={(raa) =>
          setReportFilter((prev) => ({
            ...prev,
            raa: raa as number | undefined,
          }))
        }
        minWidth={120}
        sx={{ bgcolor: "background.paper" }}
      />
      <SmaSelect
        size="small"
        includeNone
        value={reportFilter.sma}
        onChange={(sma) =>
          setReportFilter((prev) => ({
            ...prev,
            sma: sma as number | undefined,
          }))
        }
        minWidth={120}
        sx={{ bgcolor: "background.paper" }}
      />
      <SaSelect
        size="small"
        includeNone
        value={reportFilter.sa}
        onChange={(sa) =>
          setReportFilter((prev) => ({
            ...prev,
            sa: sa as number | undefined,
          }))
        }
        minWidth={120}
        sx={{ bgcolor: "background.paper" }}
      />
      <TextField
        size="small"
        label="Key Word"
        variant="outlined"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ width: { xs: "100%", sm: 180 }, bgcolor: "background.paper" }}
      />
    </>)
  }

  const GenerateTab = () => {
    if (!selectedReportData) {
      return;
    }

    const isAdmin = user?.roles?.filter((role) => userAdminRoles.includes(role)).length > 0;
    const isClioStaff = user?.roles?.filter((role) => role === ApplicationRole.CLIO_STAFF).length > 0;

    return (
      <div className="row">
        <Box sx={{ width: "100%", mb: 2, mt: 2 }}>
          <FilterBar
            hideFilterButton
            hideClearButton
            leading={
              <>
                {!!selectedReportData.showDateRange && <DateRangeSelect />}
                {selectedReport === ReportTypes.LEADS_DATE && (
                  <FormControl size="small" sx={{ width: { xs: "100%", sm: 200 }, bgcolor: "background.paper" }}>
                    <InputLabel>Practice area</InputLabel>
                    <Select
                      value={selectedPracticeArea}
                      label="Practice area"
                      onChange={(e) => setSelectedPracticeArea(String(e.target.value))}
                    >
                      <MenuItem value={AllSelection}>{AllSelection}</MenuItem>
                      <MenuItem value="Employment">Employment</MenuItem>
                      <MenuItem value="Personal Injury">Personal Injury</MenuItem>
                      <MenuItem value="Workers' Compensation">Workers' Compensation</MenuItem>
                      <MenuItem value="Class Action">Class Action</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {selectedReport === ReportTypes.OVERVIEW && isAdmin && AddSelect(true)}
                {(selectedReport === ReportTypes.TASKS ||
                  selectedReport === ReportTypes.HOURS ||
                  selectedReport === ReportTypes.SETTLEMENT_PRELIT ||
                  selectedReport === ReportTypes.SETTLEMENT_LIT ||
                  selectedReport === ReportTypes.LITIGATION_FUNNEL ||
                  selectedReport === ReportTypes.NEGOTIATION_LOG) &&
                  isAdmin &&
                  AddSelect()}
                {selectedReport === ReportTypes.LITIGATION_FUNNEL && isClioStaff && AddSelect()}
                {selectedReport === ReportTypes.LITIGATION_FUNNEL && AddLitigationFilters()}
              </>
            }
            trailing={
              <>
                <AppButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => loadData()}
                >
                  Load Report
                </AppButton>
                {selectedReport === ReportTypes.TASKS && isAdmin && (
                  <AppButton
                    variant="outlined"
                    size="small"
                    onClick={DownloadActivitiesReport}
                    sx={{ bgcolor: "background.paper" }}
                  >
                    Download Report
                  </AppButton>
                )}
                {selectedReport === ReportTypes.LEADS_ATTORNEY &&
                  user.roles.includes(ApplicationRole.ADMIN) && (
                    <AppButton
                      variant="outlined"
                      size="small"
                      onClick={DownloadAttorneyLeadsReport}
                      sx={{ bgcolor: "background.paper" }}
                    >
                      Download Report
                    </AppButton>
                  )}
                {selectedReport === ReportTypes.KPI && isAdmin && (
                  <AppButton
                    variant="outlined"
                    size="small"
                    onClick={DownloadKPIReport}
                    sx={{ bgcolor: "background.paper" }}
                  >
                    Download Report
                  </AppButton>
                )}
                {selectedReport === ReportTypes.EMPTY_MATTERS && isAdmin && (
                  <AppButton
                    variant="outlined"
                    size="small"
                    onClick={DownloadEmptyMatters}
                    sx={{ bgcolor: "background.paper" }}
                  >
                    Download Report
                  </AppButton>
                )}
                {selectedReport === ReportTypes.INACTIVE_MATTERS && isAdmin && (
                  <AppButton
                    variant="outlined"
                    size="small"
                    onClick={DownloadInactiveMatters}
                    sx={{ bgcolor: "background.paper" }}
                  >
                    Download Report
                  </AppButton>
                )}
                {selectedReport === ReportTypes.SOLES && isAdmin && (
                  <>
                    <AppButton
                      variant="outlined"
                      size="small"
                      onClick={DownloadMontlySoles}
                      sx={{ bgcolor: "background.paper" }}
                    >
                      Download Monthly Report
                    </AppButton>
                    <AppButton
                      variant="outlined"
                      size="small"
                      onClick={DownloadAttorneySoles}
                      sx={{ bgcolor: "background.paper" }}
                    >
                      Download By Attorney Report
                    </AppButton>
                  </>
                )}
                {selectedReport === ReportTypes.HOURS && isAdmin && (
                  <AppButton
                    variant="outlined"
                    size="small"
                    onClick={DownloadHourly}
                    sx={{ bgcolor: "background.paper" }}
                  >
                    Download Report
                  </AppButton>
                )}
                {selectedReport === ReportTypes.LITIGATION_FUNNEL &&
                  (isClioStaff || isAdmin) && (
                    <AppButton
                      variant="outlined"
                      size="small"
                      onClick={DownloadLitigationReport}
                      sx={{ bgcolor: "background.paper" }}
                    >
                      Download Report
                    </AppButton>
                  )}
                {selectedReport === ReportTypes.LITIGATION_FUNNEL && (
                  <AppButton
                    variant="outlined"
                    size="small"
                    onClick={() => litigationTableRef.current?.saveAllNotes()}
                    disabled={!notesDirty}
                    sx={{ bgcolor: "background.paper" }}
                  >
                    Save Notes
                  </AppButton>
                )}
              </>
            }
          />
        </Box>
        {showLeadsTotalSummary && tableData.length > 0 && (
          <div className="leads-staff-total ms-1 mb-2">
            <strong>Total leads:</strong> {leadsTotalSum}
            {selectedReport === ReportTypes.LEADS_DATE && (
              <>
                <span className="separator"> | </span>
                <strong>Completed:</strong> {leadsCompletedSum}
              </>
            )}
          </div>
        )}
        {selectedReport === ReportTypes.LITIGATION_FUNNEL ? (
          <LitigationReportTable
            ref={litigationTableRef}
            data={(filteredTableData as ReportModel[]) ?? []}
            matterOptions={matterOptions}
            onDirtyChange={setNotesDirty}
          />
        ) : (
          <div id="table-container">
            <ReactTabulator
              columns={selectedReportData.columns}
              data={updatePropertyNames(filteredTableData)}
              options={tableOptions}
              className={`css-class-${selectedReport}`}
            />
          </div>
        )}
      </div>
    );
  };

  const DownloadActivitiesReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      selectedStaff: usersList.find((user) => user.name === selectedUser)
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/downloadUsersTasksReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `UserTasks.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadAttorneyLeadsReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getAttorneyLeadsReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `RejectedLeadsByAttorney${startDate}--${endDate}.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };
  const DownloadKPIReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getKPIReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `KPI-report-${startDate}--${endDate}.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadEmptyMatters = () => {
    dispatch(startSpinner());
    fetch(`${OpenAPI.BASE}/api/File/getMattersWithOldChanges`, {
      method: "GET",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `EmptyMatters.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadInactiveMatters = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/donwloadInactiveMatters`, {
      method: "GET",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `InactiveMatters.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadMontlySoles = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getSolesMonthReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `SolesMontly.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadAttorneySoles = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getSolesAttyReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `SolesAttorney.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };
  const DownloadHourly = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
      selectedStaff: usersList.find((user) => user.name === selectedUser)
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getHoursReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `HoursReport.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const DownloadLitigationReport = () => {
    dispatch(startSpinner());
    var reportRequest = {
      type: selectedReport,
      startDate: startDate,
      endDate: endDate,
      selectedStaff: usersList.find((user) => user.name === selectedUser),
      litigationFilter: reportFilter,
    } as ReportRequest;
    fetch(`${OpenAPI.BASE}/api/File/getLitigationFunnelReport`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportRequest),
    })
      .then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `LitigationReport.xlsx`;
          document.body.appendChild(a);
          a.click();
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };
  

  if (!user && !phoneId) {
    return null;
  }
  const handleTabChange = (event, newValue: number) => {
    switch (newValue) {
      case 0: {
        setSelectedReport(ReportTypes.OVERVIEW);
        break;
      }
      case 1: {
        setSelectedReport(ReportTypes.TASKS);
        break;
      }
      case 2: {
        setSelectedPreset("Month");
        setStartDate(dayjs().startOf("month"));
        setEndDate(dayjs(new Date()));
        setSelectedReport(ReportTypes.HOURS);
        break;
      }
      case 3: {
        setSelectedReport(ReportTypes.EMPTY_MATTERS);
        break;
      }
      case 4: {
        setSelectedReport(ReportTypes.INACTIVE_MATTERS);
        break;
      }
    }
    setTab(newValue);
    setTableData([]);
  };

  return (
    <div className="reports-page">
      <PageHeader
        title={selectedReportData?.header}
        subtitle={selectedReportData?.description}
      />

      {reportType == "Overview" && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Overview"
              {...TabsHelpers.parseProps(0)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Task Activity"
              {...TabsHelpers.parseProps(1)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Hours"
              {...TabsHelpers.parseProps(2)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Empty matters"
              {...TabsHelpers.parseProps(3)}
            />
            <Tab
              hidden={props.source !== ReportSource.Clio}
              label="Inactive matters"
              {...TabsHelpers.parseProps(4)}
            />
          </Tabs>
        </Box>
      )}
      {GenerateTab()}
    </div>
  );
}
